import { EllipsisOutlined } from '@ant-design/icons'
import { App, Button, Dropdown, MenuProps, Space } from 'antd'
import { FC, useRef, useState } from 'react'
import DownloadShipmentBlank from '../../../../components/buttons/download-shipment-blank/download-shipment-blank'
import TaskCreationWizard from '../../../../components/task-creation-wizard/task-creation-wizard'
import { SupportedTaskCreationType } from '../../../../components/task-creation-wizard/types'
import useEndpointQueryParams from '../../../../hooks/table-pages/use-endpoint-query-parameters'
import useErrorModal from '../../../../hooks/api/use-error-modal'
import {
    shipmentsApi,
    useCreateAssignmentApprovalTaskMutation,
    useLazyGetShipmentByDocIdQuery,
} from '../../../../store/api/shipments-api'
import { tasksApi } from '../../../../store/api/tasks-api'
import { useAppDispatch } from '../../../../store/store'
import Task from '../../../../types/api/task'
import { defaultParams } from '../../constants/default-params'
import { shipmentApprovalQueryParams } from '../../constants/shipment-approval-query'
import { deserializeFilters } from '../../helpers/deserialize-filters'
import { DataSourceItem } from '../../shipments-page'
import AddShipmentToCommonScheduleQueue from './add-shipment-to-common-schedule-queue'
import {
    ASSIGNMENT_APPROVAL_TASK_TYPE,
    getAssignmentApprovalAction,
} from '../../../../helpers/entities/shipment/curator-approval'

interface ShipmentActionsProps {
    row: DataSourceItem
}

type WizardState = {
    open: boolean
    type: SupportedTaskCreationType
}

const ShipmentActions: FC<ShipmentActionsProps> = ({ row }) => {
    const { message } = App.useApp()
    const dispatch = useAppDispatch()
    const params = useEndpointQueryParams({ deserializeFilters })
    const queryArg = { ...params, ...defaultParams }
    const [wizard, setWizard] = useState<WizardState | null>(null)
    const [createApprovalTask, createApprovalTaskState] = useCreateAssignmentApprovalTaskMutation()
    const approvalRequestLock = useRef(false)
    const [getApprovalState, { data: approvalStateResponse, isFetching: isApprovalStateFetching }] =
        useLazyGetShipmentByDocIdQuery()

    useErrorModal(createApprovalTaskState, 'Не удалось отправить задание на согласование')

    const approvalAction = getAssignmentApprovalAction(
        isApprovalStateFetching ? row : (approvalStateResponse?.data ?? row)
    )

    const markTaskCreated = (taskType: Task['type']) => {
        dispatch(
            shipmentsApi.util.updateQueryData('getShipments', queryArg, draft => {
                const item = draft.data.find(shipment => shipment.documentId === row.documentId)
                if (!item) return
                if (
                    taskType === 'Отрисовка бланка' &&
                    item.tasks?.some(task => task.type === 'Отрисовка бланка')
                ) {
                    return
                }
                item.tasks = [...(item.tasks ?? []), { type: taskType } as Task]
            })
        )
    }

    const syncApprovalCache = () => {
        dispatch(
            shipmentsApi.util.updateQueryData('getShipments', queryArg, draft => {
                const item = draft.data.find(shipment => shipment.documentId === row.documentId)
                if (!item) return
                if (approvalAction === 'create') {
                    const alreadyHas = item.tasks?.some(
                        task => task.type === ASSIGNMENT_APPROVAL_TASK_TYPE
                    )
                    if (!alreadyHas) {
                        item.tasks = [
                            ...(item.tasks ?? []),
                            { type: ASSIGNMENT_APPROVAL_TASK_TYPE } as Task,
                        ]
                    }
                }
                item.approved = null
            })
        )
        dispatch(tasksApi.util.invalidateTags(['Tasks']))
    }

    const openWizard = (type: SupportedTaskCreationType) => {
        setWizard({ open: true, type })
    }

    const closeWizard = () => {
        setWizard(null)
    }

    const onCreateAssignmentApproval = async () => {
        if (approvalRequestLock.current || createApprovalTaskState.isLoading) return
        approvalRequestLock.current = true
        try {
            const res = await createApprovalTask(row.documentId)
            if (!('error' in res)) {
                message.success(
                    approvalAction === 'reopen'
                        ? 'Задача снова на согласовании'
                        : 'Задача «Согласование задания» создана'
                )
                syncApprovalCache()
            }
        } finally {
            approvalRequestLock.current = false
        }
    }

    const onMenuOpenChange = (nextOpen: boolean) => {
        if (!nextOpen || !row.documentId) return
        void getApprovalState([row.documentId, shipmentApprovalQueryParams], false)
    }

    const items: MenuProps['items'] = [
        {
            key: 'drawing-blank',
            label: 'Создать задачу на отрисовку бланка',
            onClick: () => openWizard('Отрисовка бланка'),
        },
        {
            key: 'form-reorder',
            label: 'Создать задачу на заказ форм',
            onClick: () => openWizard('Перезаказ форм'),
        },
        ...(approvalAction === 'none'
            ? []
            : [
                  {
                      key: 'assignment-approval',
                      disabled: createApprovalTaskState.isLoading || isApprovalStateFetching,
                      label:
                          approvalAction === 'reopen'
                              ? 'Вернуть на согласование'
                              : 'Создать задачу на согласование задания',
                      onClick: () => {
                          void onCreateAssignmentApproval()
                      },
                  },
              ]),
    ]

    return (
        <>
            <Space>
                <DownloadShipmentBlank
                    shipment={row}
                    blankType='client'
                    blankPreviewFiles={row.blankPreviewFiles}
                />
                <AddShipmentToCommonScheduleQueue shipment={row} />
                <Dropdown menu={{ items }} trigger={['click']} onOpenChange={onMenuOpenChange}>
                    <Button size='small' icon={<EllipsisOutlined />} title='Создать задачу' />
                </Dropdown>
            </Space>
            {wizard && (
                <TaskCreationWizard
                    open={wizard.open}
                    type={wizard.type}
                    initialValues={{ shipment: row }}
                    onClose={closeWizard}
                    onSuccess={() => markTaskCreated(wizard.type)}
                />
            )}
        </>
    )
}

export default ShipmentActions
